import { useState } from "react";
import Modal, { ModalBody, ModalFooter } from "@components/ui/Modal";
import Button from "@components/ui/Button";
import { submitFeedback } from "@services/feedback";
import { sanitizeFormValues } from "@utils/sanitize";

const CATEGORIES = [
  { value: "bug", label: "Bug Report" },
  { value: "feature_request", label: "Feature Request" },
  { value: "general", label: "General Feedback" },
];

export default function FeedbackModal({ show, onClose }) {
  const [category, setCategory] = useState("bug");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const resetForm = () => {
    setCategory("bug");
    setDescription("");
    setError("");
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (description.trim().length < 10) {
      setError("Description must be at least 10 characters.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await submitFeedback(sanitizeFormValues({ category, description: description.trim() }));
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onClose={handleClose} title="Report a Problem" size="md">
      <ModalBody>
        {success ? (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Thank you!</h3>
            <p className="text-sm text-gray-500 mt-1">Your feedback has been sent to the development team.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue or your request in detail..."
                rows={5}
                maxLength={2000}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {description.length}/2000
              </p>
            </div>

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>
        )}
      </ModalBody>

      {!success && (
        <ModalFooter className="flex justify-end gap-3">
          <Button color="light" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || description.trim().length < 10}
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}
